import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import usePrevious from 'common/usePrevious';
import { connect } from 'react-redux';
import { Button, Form, Spin, Popover } from 'grab-ui';
import I18n from 'i18n-js';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _sumBy from 'lodash/sumBy';
import allErrorFixedFig from 'public/images/all-errors-fixed.svg';
import NewBulkUploadDuck from 'ducks/new-bulk-upload-duck';
import scribeNewBulkUploadDuck from 'ducks/scribe-new-bulk-upload-duck';
import { selectedCitySelector } from 'selectors/app-selector';
import {
  bulkUploadPOIsInitiatedSelector,
  bulkUploadRawDataSelector,
} from 'selectors/new-bulk-upload-selectors';
import FloatingFooter from 'components/floating-footer';
import cx from 'classnames';
import CheckUploadTable from './check-upload-table';
import EditAddressDrawer from './edit-address-drawer';
import styles from './check-upload.less';
import BulkUploadPageContext from '../common/context';
import { noBatchRowOrFirstRowInBatch } from './helper';

const CheckUpload = ({ dispatch, rawData, form, city, POIsInitiated }) => {
  const { stepForwardFunc } = useContext(BulkUploadPageContext);

  const { validateFields, getFieldsError } = form;
  const [formInitiated, setFormInitiated] = useState(false);
  const [errorView, setErrorView] = useState(false);
  const [editingAddress, setEditingAddress] = useState(undefined);

  const onViewAllRows = useCallback(
    () => {
      dispatch(
        scribeNewBulkUploadDuck.creators.scribeExpressNewBulkUploadStep2SelectAllRows(),
      );
      setErrorView(false);
    },
    [setErrorView],
  );

  const onViewErrorRows = useCallback(
    () => {
      setErrorView(true);
      dispatch(
        scribeNewBulkUploadDuck.creators.scribeExpressNewBulkUploadStep2SelectRowsWithError(),
      );
    },
    [setErrorView],
  );

  const rowIDsWithError = useMemo(
    () => {
      const errors = getFieldsError();
      const rowIDsWithError = {};
      Object.entries(errors).forEach(([fieldName, error]) => {
        if (error && error.length > 0) {
          const splits = String(fieldName).split('/');
          rowIDsWithError[splits[0]] = true;
        }
      });
      rawData.forEach(row => {
        const { id, pickUpPOI, dropOffPOI } = row;
        if (!_get(pickUpPOI, 'id')) {
          if (noBatchRowOrFirstRowInBatch(row)) {
            rowIDsWithError[id] = true;
          }
        }
        if (!_get(dropOffPOI, 'id')) {
          rowIDsWithError[id] = true;
        }
      });
      return rowIDsWithError;
    },
    [getFieldsError, rawData],
  );
  const errorRowCount = Object.keys(rowIDsWithError).length;

  const prevPOIsInitiated = usePrevious(POIsInitiated);
  useEffect(
    () => {
      if (!prevPOIsInitiated && POIsInitiated) {
        setTimeout(() => {
          validateFields && validateFields();
          setFormInitiated(true);
        }, 100);
      }
    },
    [
      prevPOIsInitiated,
      POIsInitiated,
      dispatch,
      validateFields,
      setFormInitiated,
    ],
  );

  useEffect(
    () => {
      if (city) {
        dispatch(NewBulkUploadDuck.creators.fetchPOIsForRawData());
      }
    },
    [dispatch, city],
  );

  const onConfirmUpload = useCallback(
    () => {
      if (validateFields) {
        validateFields().then(() => {
          dispatch(NewBulkUploadDuck.creators.createNewDeliveries());
          dispatch(
            scribeNewBulkUploadDuck.creators.scribeExpressNewBulkUploadStep2Confirm(),
          );
          dispatch(NewBulkUploadDuck.creators.groupingResetState());
          stepForwardFunc();
        });
      }
    },
    [dispatch, validateFields, stepForwardFunc, rawData],
  );

  const confirmButtonWithPopover = useMemo(
    () => {
      const confirmButton = (
        <div className={styles.buttonsWrapper}>
          <Button
            className={styles.confirmButton}
            type={'primary'}
            onClick={onConfirmUpload}
            disabled={!formInitiated || errorRowCount > 0}
          >
            {I18n.t(
              'phrase.geWebBulkUpload.express_web_bulk_upload_button_confirm_changes_error_in_excel',
            )}
          </Button>
        </div>
      );

      return errorRowCount > 0 ? (
        <Popover
          trigger={'hover'}
          placement={'top'}
          content={I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_body_fix_rows_before_proceeding__error_in_excel',
          )}
          overlayClassName={styles.buttonPopover}
        >
          {confirmButton}
        </Popover>
      ) : (
        confirmButton
      );
    },
    [errorRowCount, formInitiated, onConfirmUpload],
  );

  useEffect(
    () => {
      if (formInitiated) {
        const errors = getFieldsError();
        const errorCodes = [];
        Object.entries(errors).forEach(([fieldName, error]) => {
          if (error && error.length > 0) {
            const errorFieldName = String(fieldName).split('/')[1];
            errorCodes.push(`${errorFieldName}_${error}`);
          }
        });
        const districtUsage = _sumBy(rawData, row => {
          const { senderAdminArea, receiverAdminArea } = row;
          return (
            (noBatchRowOrFirstRowInBatch(row) && senderAdminArea ? 1 : 0) +
            (receiverAdminArea ? 1 : 0)
          );
        });
        const totalAddressCount = _sumBy(
          rawData,
          row => (noBatchRowOrFirstRowInBatch(row) ? 2 : 1),
        );
        dispatch(
          scribeNewBulkUploadDuck.creators.scribeExpressNewBulkUploadStep2Default(
            _get(rawData, 'length'),
            errorCodes,
            districtUsage,
            totalAddressCount,
          ),
        );
      }
    },
    [formInitiated],
  );

  return (
    <div className={styles.checkUpload}>
      {!POIsInitiated && <Spin size={'large'} className={styles.loading} />}
      <div className={POIsInitiated ? undefined : styles.hidden}>
        <div className={styles.guidance}>
          <div className={styles.numRecipient}>
            {I18n.t(
              'phrase.geWebBulkUpload.express_web_bulk_upload_heading_amt_of_recipient_happypath',
              {
                amountOfRecipient: _get(rawData, 'length', 0),
              },
            )}
          </div>
          <div className={styles.description}>
            {errorRowCount > 0
              ? I18n.t(
                  'phrase.geWebBulkUpload.express_web_bulk_upload_body_errors_detected_error_in_excel',
                )
              : I18n.t(
                  'phrase.geWebBulkUpload.express_web_bulk_upload_body_check_info_happypath',
                )}
          </div>
        </div>
        <div className={styles.filters}>
          <Button
            className={cx(styles.filter, { [styles.active]: !errorView })}
            type={'primary'}
            onClick={onViewAllRows}
          >
            {I18n.t(
              'phrase.geWebBulkUpload.express_web_bulk_upload_label_all_rows_error_in_excel',
              {
                x: rawData.length,
              },
            )}
          </Button>
          <Button
            className={cx(styles.filter, { [styles.active]: errorView })}
            type={'primary'}
            onClick={onViewErrorRows}
          >
            {I18n.t(
              'phrase.geWebBulkUpload.express_web_bulk_upload_label_rows_with_error_error_in_excel',
              {
                x: errorRowCount,
              },
            )}
          </Button>
        </div>
        {errorView && errorRowCount === 0 ? (
          <div className={styles.noErrorView}>
            <img src={allErrorFixedFig} alt="" />
            <h2 className={styles.title}>
              {I18n.t('bulkUploadRevamp.step2.noErrorView.title')}
            </h2>
            <p className={styles.description}>
              {I18n.t('bulkUploadRevamp.step2.noErrorView.description')}{' '}
            </p>
          </div>
        ) : (
          <CheckUploadTable
            form={form}
            rawData={rawData}
            rowIDsWithError={(errorView && rowIDsWithError) || undefined}
            setEditingAddress={setEditingAddress}
          />
        )}
      </div>
      <FloatingFooter visible>{confirmButtonWithPopover}</FloatingFooter>
      <EditAddressDrawer
        form={form}
        editingAddress={editingAddress}
        rawData={rawData}
        setEditingAddress={setEditingAddress}
      />
    </div>
  );
};

CheckUpload.defaultProps = {
  city: undefined,
};

CheckUpload.propTypes = {
  dispatch: PropTypes.func.isRequired,
  rawData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  form: PropTypes.shape({}).isRequired,
  city: PropTypes.shape({}),
  POIsInitiated: PropTypes.bool.isRequired,
};

const onFieldsChange = ({ dispatch }, changedFields, _) => {
  const rowChangesMap = {};
  Object.entries(changedFields).forEach(([fn, fv]) => {
    const [rowID, fieldName] = String(fn).split('/');
    _set(rowChangesMap, [rowID, fieldName], fv.value);
  });
  dispatch(NewBulkUploadDuck.creators.bulkEditRawData(rowChangesMap));
};

export default connect(state => ({
  rawData: bulkUploadRawDataSelector(state),
  city: selectedCitySelector(state),
  POIsInitiated: bulkUploadPOIsInitiatedSelector(state),
}))(Form.create({ onFieldsChange })(CheckUpload));
